import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading, { type HeadingTagsType } from '../heading/heading';
import Link from '../link/link';
import Container from '../container/container';
import Text from '../text/text';
import Image from '../image/image';

export interface VerticalHeroProps {
  /** Allows the component to get additional styles. */
  className?: string;
  ctas?: Lucid.Directus.Generated.Links[];
  header: string | string[];
  subheader?: string;
  backgroundImage?: Lucid.Directus.Generated.Directus_Files;
  noSidePadding?: boolean;
  isImageFullWidth?: boolean;
  /** The heading variant */
  headingVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The heading level */
  headingLevel?: HeadingTagsType;
}

const VerticalHero: FC<PropsWithChildren<VerticalHeroProps>> = ({
  className,
  ctas,
  header,
  headingVariant = 'hero2XL700',
  headingLevel = 'h2',
  subheader,
  backgroundImage,
  noSidePadding,
  isImageFullWidth,
  ...rest
}) => (
  <Box
    {...rest}
    as="section"
    css={theme => ({
      paddingTop: theme.space.s40,
    })}
    // eslint-disable-next-line @stylistic/jsx-sort-props
    className={className}
  >
    <Container
      css={theme => ({
        label: 'container',
        marginBottom: theme.space.s104,
        [theme.breakpoints.bp0.max]: {
          paddingLeft: noSidePadding ? 0 : undefined,
          paddingRight: noSidePadding ? 0 : undefined,
        },
        [theme.breakpoints.bp1024.min]: {
          display: 'flex',
          justifyContent: 'center',
        },
      })}
      extraPadding={3}
    >
      <Box css={{ label: 'verticalHero', textAlign: 'center' }}>
        <Heading
          as={headingLevel}
          css={theme => ({
            margin: '0 auto',
            maxWidth: '30rem',
            textAlign: 'center',
            [theme.breakpoints.bp900.min]: {
              maxWidth: '40rem',
              margin: `0 auto ${backgroundImage ? `${theme.space.s64}px` : `${theme.space.s40}px`}`,
            },
            [theme.breakpoints.bp1220.min]: {
              maxWidth: '36.25rem',
            },
          })}
          dataElementPageLocation="hero"
          variant={headingVariant}
        >
          {header}
        </Heading>
        {backgroundImage && backgroundImage.filename_disk && (
          <Image
            css={theme => ({
              objectFit: 'contain',
              marginBottom: theme.space.s24,
              width: '100%',
              [theme.breakpoints.bp500.max]: {
                width: isImageFullWidth ? '100vw' : '100%',
                maxWidth: isImageFullWidth ? '100vw' : '100%',
                transform: isImageFullWidth ? 'translateX(-1.875rem)' : 'none',
              },
            })}
            dataElementPageLocation="hero"
            imageAlt={backgroundImage?.translations?.[0]?.alt || ''}
            // Not sure, but the name background image feels wrong here when this is the only image for this component
            imageSrc={withImage(backgroundImage)}
            loading="lazy"
          />
        )}
        {subheader && (
          <Box
            css={theme => ({
              paddingBottom: theme.space.s40,
              paddingTop: theme.space.s0,
              [theme.breakpoints.bp768.min]: {
                paddingTop: theme.space.s0,
              },
            })}
          >
            <Text
              css={{
                margin: '0 auto',
                maxWidth: '40rem',
                computer: '36.25rem',
              }}
              dataElementPageLocation="hero"
              id="heroLinkDescription"
              variant="paragraphM400"
            >
              {subheader}
            </Text>
          </Box>
        )}
        {ctas &&
          ctas.map((cta, index) => (
            <Link
              key={index}
              aria-describedby="heroLinkDescription"
              css={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}
              href={cta.final_url}
              iconAnimation="navigational"
            >
              {cta.default_link_text}
            </Link>
          ))}
      </Box>
    </Container>
  </Box>
);

export { VerticalHero };
export default VerticalHero;
